import { useEffect, useState, useRef } from "react";
import { Tarif } from "@/widgets";
import { useAddUserActionMutation } from "@/store/usersApi";
import { useGetServicesMutation } from "@/store/paymentApi";
import { tg } from "@/constants/telegram";
import { useYandexMetrika, GOALS } from "@/hooks/useYandexMetrika";
import { useNavigate } from "react-router-dom";
import BackArrow from "@/shared/assets/icons/back-arrow.png";
import styles from "./SubscriptionPage.module.scss";
import { useAppSelector } from "@/shared/hooks";

type PlanCardData = {
  tarif: Tarif;
  title: string;
  monthPriceLabel: string;
  perDayLabel: string;
  oldPriceLabel?: string;
  badgeLabel?: string;
  isFree?: boolean;
};

const hasActiveDiscount = (value: string | number | null | undefined): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "number") {
    return value > 0;
  }

  if (typeof value === "string") {
    return value.trim() !== "" && value !== "0" && value !== "00:00:00" && value !== "PT0S";
  }

  return false;
};

const parseDiscountRemainingToSeconds = (value: string | number): number | null => {
  if (typeof value === "number") {
    return value > 0 ? Math.floor(value) : null;
  }

  if (value.startsWith("PT")) {
    const isoRegex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const match = value.match(isoRegex);
    if (match) {
      const hours = parseInt(match[1] || "0", 10);
      const minutes = parseInt(match[2] || "0", 10);
      const seconds = parseInt(match[3] || "0", 10);
      const total = hours * 3600 + minutes * 60 + seconds;
      return total > 0 ? total : null;
    }
  }

  if (value.includes(":")) {
    const parts = value.split(":").map(Number);
    if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
      const total = parts[0] * 3600 + parts[1] * 60 + parts[2];
      return total > 0 ? total : null;
    }
  }

  const parsed = parseFloat(value);
  if (!Number.isNaN(parsed) && parsed > 0) {
    return Math.floor(parsed);
  }

  return null;
};

const formatPrice = (value: number): string => value.toLocaleString("ru-RU");

const buildFreeTermLabel = (term: number | null | undefined): string => {
  if (!term) return "без срока";
  return `${term} дн.`;
};

const resolveBillingDays = (tarif: Tarif): number => {
  const normalizedTitle = tarif.title.toLowerCase();

  const weekMatch = normalizedTitle.match(/(\d+)\s*(нед|week)/i);
  if (weekMatch) {
    return Number(weekMatch[1]) * 7;
  }

  const monthMatch = normalizedTitle.match(/(\d+)\s*(мес|month)/i);
  if (monthMatch) {
    return Number(monthMatch[1]) * 30;
  }

  const yearMatch = normalizedTitle.match(/(\d+)\s*(год|лет|year)/i);
  if (yearMatch) {
    return Number(yearMatch[1]) * 365;
  }

  if (tarif.term > 0) {
    return tarif.term * 30;
  }

  return 30;
};

const buildPeriodLabel = (tarif: Tarif): string => {
  const normalizedTitle = tarif.title.toLowerCase();

  if (/(нед|week)/i.test(normalizedTitle)) {
    return "НЕД";
  }

  if (/(год|лет|year)/i.test(normalizedTitle)) {
    return "ГОД";
  }

  return "МЕС";
};

const resolveDiscountBadge = (current: number, previous: number): string => {
  if (previous <= current) {
    return "-0%";
  }

  const exactPercent = ((previous - current) / previous) * 100;
  const roundedToTen = Math.floor(exactPercent / 10) * 10;
  return `-${Math.max(10, roundedToTen)}%`;
};

export const SubscriptionPage = () => {

  const [addUserAction] = useAddUserActionMutation();
  const [getServices] = useGetServicesMutation();
  const [subscriptionsList, setSubscriptionsList] = useState<Tarif[]>([]);
  const [selectedTarifId, setSelectedTarifId] = useState<number | null>(null);
  const [discountSecondsLeft, setDiscountSecondsLeft] = useState<number | null>(null);
  const { trackAction } = useYandexMetrika();
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const hasSentAction = useRef(false);
  
  useEffect(() => {
    const loadServices = async () => {
      const hasAuth = tg?.initData || isAuthenticated || localStorage.getItem("access_token");
      if (hasAuth) {
        try {
          const response = await getServices().unwrap();
          const { services } = response;
          setSubscriptionsList(services);
        } catch (error) {
          console.error("Ошибка загрузки сервисов:", error);
        }
      }
    };
    loadServices();
  }, [getServices, isAuthenticated]);

  useEffect(() => {
    if (hasSentAction.current || subscriptionsList.length === 0) return;
    
    const sendAction = async () => {
      const hasAuth = tg?.initData || isAuthenticated || localStorage.getItem("access_token");
      if (hasAuth) {
        try {
          await addUserAction({
            action: "Открыл страницу тарифов"
          }).unwrap();
          
          trackAction(GOALS.ACTION_SUBSCRIPTION_VIEWED, {
            subscriptionsCount: subscriptionsList.length
          });
          
          hasSentAction.current = true;
        } catch (error) {
          console.error("Ошибка при отправке действия пользователя:", error);
        }
      }
    };

    sendAction();
  }, [subscriptionsList.length, addUserAction, isAuthenticated, trackAction]);

  const apiTarifs = subscriptionsList;
  const sortedTarifs = [...apiTarifs].sort((a, b) => {
    const aIsFree = a.cost === 0;
    const bIsFree = b.cost === 0;
    if (aIsFree && !bIsFree) return -1;
    if (!aIsFree && bIsFree) return 1;
    return (a.term ?? 9999) - (b.term ?? 9999);
  });

  const planCards: PlanCardData[] = sortedTarifs.map((tarif) => {
    const isFree = tarif.cost === 0;
    const freeTermLabel = buildFreeTermLabel(tarif.term);
    return {
      tarif,
      title: tarif.title,
      monthPriceLabel: isFree
        ? "0 ₽"
        : `${formatPrice(tarif.cost)} ₽ / ${buildPeriodLabel(tarif)}`,
      perDayLabel: isFree
        ? freeTermLabel
        : `${Math.floor(tarif.cost / resolveBillingDays(tarif))} ₽ / день`,
      oldPriceLabel: !isFree && tarif.prev_price ? `${formatPrice(tarif.prev_price)} ₽` : undefined,
      badgeLabel: isFree
        ? "бесплатно"
        : tarif.prev_price && tarif.prev_price > tarif.cost
          ? resolveDiscountBadge(tarif.cost, tarif.prev_price)
          : undefined,
      isFree
    };
  });

  useEffect(() => {
    if (planCards.length === 0) {
      setSelectedTarifId(null);
      return;
    }

    const preferred = planCards[0].tarif.id;
    setSelectedTarifId((prev) => {
      if (prev && planCards.some((plan) => plan.tarif.id === prev)) {
        return prev;
      }
      return preferred;
    });
  }, [planCards]);

  const discountTarif = planCards
    .map((plan) => plan.tarif)
    .find((tarif) => hasActiveDiscount(tarif.discount_remaining));

  useEffect(() => {
    if (!discountTarif || !hasActiveDiscount(discountTarif.discount_remaining)) {
      setDiscountSecondsLeft(null);
      return;
    }

    const parsedSeconds = parseDiscountRemainingToSeconds(discountTarif.discount_remaining as string | number);
    if (!parsedSeconds) {
      setDiscountSecondsLeft(null);
      return;
    }

    const startTimestamp = Date.now();
    setDiscountSecondsLeft(parsedSeconds);

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimestamp) / 1000);
      const remaining = parsedSeconds - elapsed;
      setDiscountSecondsLeft(remaining > 0 ? remaining : null);
    }, 1000);

    return () => clearInterval(interval);
  }, [discountTarif]);

  const handleSubscriptionClick = (tarifId: number) => {
    navigate(`/subscription/${tarifId}`);
  };

  const handleBack = () => {
    navigate("/more");
  };

  const selectedTarif = planCards.find((plan) => plan.tarif.id === selectedTarifId)?.tarif;

  const timerHours = discountSecondsLeft ? Math.floor(discountSecondsLeft / 3600) : 0;
  const timerMinutes = discountSecondsLeft ? Math.floor((discountSecondsLeft % 3600) / 60) : 0;
  const timerSeconds = discountSecondsLeft ? discountSecondsLeft % 60 : 0;
  const formatTime = (value: number) => value.toString().padStart(2, "0");

  return (
    <div data-testid="SubscriptionPage" className={styles.subscriptionPage}>
      <main className={styles.main}>
        <header className={styles.header}>
          <button onClick={handleBack} className={styles.backButton} aria-label="Назад">
            <img
              src={BackArrow}
              alt="Назад"
              className={styles.backIcon}
            />
          </button>
        </header>

        <h1 className={styles.heading}>Подпишись и открой полной доступ</h1>

        <div className={styles.featuresWrap}>
          <div className={styles.featureChipWide}>
            <span>Готовые</span>
            <span className={styles.featureEmoji}>🎥</span>
            <span>видео тренировок</span>
          </div>
          <div className={styles.featuresRow}>
            <div className={styles.featureChip}>
              <span className={styles.featureEmoji}>✨</span>
              <span>AI-тренер</span>
            </div>
            <div className={styles.featureChip}>
              <span>доступ ко всем функциям</span>
            </div>
          </div>
        </div>

        {discountSecondsLeft && (
          <section className={styles.discountBlock}>
            <div className={styles.discountHeader}>СКИДКА ЗАКАНЧИВАЕТСЯ ЧЕРЕЗ</div>
            <div className={styles.discountTimer}>
              <div className={styles.timerBox}>{formatTime(timerHours)}</div>
              <span className={styles.timerSeparator}>:</span>
              <div className={styles.timerBox}>{formatTime(timerMinutes)}</div>
              <span className={styles.timerSeparator}>:</span>
              <div className={styles.timerBox}>{formatTime(timerSeconds)}</div>
            </div>
          </section>
        )}

        <section className={styles.tarifsList}>
          {planCards.map((plan) => (
            <button
              key={plan.tarif.id}
              className={`${styles.tarifCard} ${selectedTarifId === plan.tarif.id ? styles.tarifCardSelected : ""} ${plan.isFree ? styles.tarifCardFree : ""}`}
              onClick={() => setSelectedTarifId(plan.tarif.id)}
              type="button"
            >
              <div className={styles.tarifLeft}>
                <span className={styles.tarifTitle}>{plan.title}</span>
                <div className={styles.tarifSubLine}>
                  {plan.oldPriceLabel && <span className={styles.tarifOldPrice}>{plan.oldPriceLabel}</span>}
                  <span className={`${styles.tarifMonthPrice} ${plan.isFree ? styles.tarifMonthPriceFree : ""}`}>{plan.monthPriceLabel}</span>
                </div>
              </div>
              <div className={styles.tarifRight}>
                {plan.badgeLabel && <span className={`${styles.discountBadge} ${plan.isFree ? styles.freeBadge : ""}`}>{plan.badgeLabel}</span>}
                <span className={`${styles.tarifPerDay} ${plan.isFree ? styles.tarifPerDayFree : ""}`}>{plan.perDayLabel}</span>
              </div>
            </button>
          ))}
        </section>

        <p className={styles.resultHint}>Оптимальный результат достигается через 3 месяца</p>

        <button
          className={styles.ctaButton}
          onClick={() => selectedTarif && handleSubscriptionClick(selectedTarif.id)}
          type="button"
          disabled={!selectedTarif}
        >
          Получить мой план
        </button>
      </main>
    </div>
  );
};

